/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Typography,
  Grid,
  Box
} from '@material-ui/core';

import { LoadingHandler, StateErrorDisplay, shapes } from '@greenville/framework';
import { toJS } from 'mobx';
import { FormattedMessage } from 'react-intl';

import UserPrivilege from './UserPrivilege';
import Users from '../models/Users';
import UserDialog from './UserDialog';
import Roles from '../models/Roles';
import Loader from '../../../common/Loader';
import ValidatePermissions from '../ValidatePermissions';
import TooltipCustom from '../../../common/components/TooltipCustom';
import UsersPermissions from '../models/UsersPermissions';
import RoleDialog from './RoleDialog';
import RoleList from './RoleList';
import AllRoles from '../models/AllRoles';
import { styles } from './UserStyle';

/**
 * A Component for the display of the Course Card.
 *
 * @param data the title of the course
 * @param btnText the title of the course
 * @param lastName the title of the course
 * @param role the title of the course
 * @param edit the title of the course
 * @return {*} the React pure component
 * @constructor
 */

const columns = [
  {
    id: 'userLogin',
    label: 'User Login',
    minWidth: 70,
    align: 'center',
    format: (value) => value.toLocaleString()
  },
  {
    id: 'firstName',
    label: 'First Name',
    minWidth: 120,
    align: 'center',
    format: (value) => value.toLocaleString()
  },
  {
    id: 'lastName',
    label: 'Last Name',
    minWidth: 120,
    align: 'center',
    format: (value) => value.toLocaleString()
  },
  {
    id: 'email',
    label: 'Email',
    minWidth: 120,
    align: 'center',
    format: (value) => value.toLocaleString()
  },
  {
    id: 'role',
    label: 'Role',
    minWidth: 120,
    align: 'center',
    format: (value) => value.toLocaleString()
  },
  {
    id: 'action',
    label: 'Action',
    minWidth: 120,
    align: 'center',
    variant: 'outlined',
    color: 'primary',
    size: 'small',
    format: (value) => value.toLocaleString()
  }
];

const UsersList = ({
  classes,
  data,
  usersState,
  updateBreadcrumbs,
  pathUrl,
  userRoles,
  match,
  addUserStatus,
  editUserstatus,
  updateRolesStatus,
  language,
  userPermissions,
  allRoles,
  addRoleStatus
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [openUserPrivilegeDialog, setopenUserPrivilegeDialog] = React.useState(false);
  const [showAddUserText, setshowAddUserText] = React.useState(false);
  const [showEditData, setshowEditData] = React.useState({});
  const [openNewUserDialog, setOpenNewUserDialog] = React.useState(false);
  const permissionsList = toJS(userPermissions.permissionList.permissions);
  const [searchField, setSearchField] = React.useState('');
  const [filteredData, setFilteredData] = React.useState(data);
  const [openNewRoleDialog, setOpenNewRoleDialog] = React.useState(false);
  const [openRoleList, setOpenRoleList] = React.useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const history = useHistory();

  const closeUserDialog = () => {
    updateBreadcrumbs('List');
    pathUrl('');
    setOpenNewUserDialog(false);
  };

  const handleAddUserClick = () => {
    updateBreadcrumbs('Add');
    pathUrl('add');
    setOpenNewUserDialog(true);
  };

  const closeShowAddEdit = () => {
    updateBreadcrumbs('List');
    pathUrl('');
    setopenUserPrivilegeDialog(false);
  };

  const closeUploadContent = () => {
    setopenUserPrivilegeDialog(false);
  };

  const saveUser = (userData) => {
    usersState.addUser(userData);
    setOpenNewUserDialog(false);
  };

  const updateUser = (userData) => {
    usersState.updateUser(userData);
    setshowAddUserText(false);
  };

  const UserEditData = (userid) => {
    usersState.userList.map((x) => {
      if (x.userId === userid) {
        return setshowEditData(x);
      }
      return null;
    });
  };

  const handleEditData = (row, column) => {
    if (column.id === 'action') {
      setshowAddUserText(true);
      UserEditData(row.userId);
      updateBreadcrumbs('Edit');
      pathUrl('edit');
      setOpenNewUserDialog(true);
    }
  };

  React.useEffect(() => {
    setFilteredData(data);
    setSearchField('');
  }, [data]);

  const handleSearchClick = () => {
    const q = searchField.trim().toLowerCase();
    if (!q) {
      setFilteredData([]);
      return;
    }
    const results = data.filter(
      (row) => row.userLogin.toLowerCase().includes(q) || row.email.toLowerCase().includes(q)
    );
    setFilteredData(results);
  };

  const closeRoleDialog = () => {
    setOpenNewRoleDialog(false);
  };

  const handleAddRoleClick = () => {
    setOpenNewRoleDialog(true);
  };
  const handleEditRole = () => {
    setOpenRoleList(true);
  };

  const closeRoleList = () => {
    setOpenRoleList(false);
  };

  const saveRole = (rolesData) => {
    usersState.addRoles(rolesData);
    setOpenNewRoleDialog(false);
  };
  return (
    <ValidatePermissions givenPermissions="user_can_view">
      <>
        {history.location.pathname === '/users' && (
          <div>
            <Paper elevation={3}>
              <Box sx={{ flexGrow: 1, p: 2 }}>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                  className={classes.searchHeaders}
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      label="Search with User Login or email"
                      variant="outlined"
                      size="small"
                      className={classes.searchBarClass}
                      onChange={(e) => setSearchField(e.target.value)}
                      value={searchField}
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      id="searchBtn"
                      className={classes.addUser}
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleSearchClick}
                    >
                      SEARCH
                    </Button>
                  </Grid>

                  <Grid item>
                    <TooltipCustom
                      title={
                        !permissionsList.includes('ingestion_can_edit') ? (
                          <FormattedMessage {...language.getText('user.PERMISSIONS_LABEL')} />
                        ) : (
                          <FormattedMessage {...language.getText('user.ADD_USER')} />
                        )
                      }
                    >
                      <span>
                        <Button
                          id="newUserBtn"
                          className={classes.addUser}
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleAddUserClick}
                          disabled={!permissionsList.includes('user_can_edit')}
                        >
                          ADD USER
                        </Button>
                      </span>
                    </TooltipCustom>
                  </Grid>
                  <Grid item>
                    <Button
                      id="searchBtn"
                      className={classes.addUser}
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleAddRoleClick}
                    >
                      ADD ROLE
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      id="searchBtn"
                      className={classes.addUser}
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleEditRole}
                    >
                      EDIT ROLE
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <br />
            <br />
            <Paper className={classes.root}>
              <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          className={classes.fontSize}
                          key={column.id}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => (
                      <TableRow hover role="checkbox" tabIndex={-1} key={`$(row.id)-${i}`}>
                        {columns.map((column, index) => {
                          const value = row[column.id];
                          return (
                            <TableCell
                              key={`$(column.id)-${index}`}
                              align={column.align}
                              onClick={() => handleEditData(row, column)}
                            >
                              {column.format && typeof value === 'number' ? column.format(value) : value}
                              {column.id === 'role' ? (
                                <Typography
                                  onClick={() => setopenUserPrivilegeDialog(true)}
                                  variant="subtitle2"
                                  className={classes.posClass}
                                >
                                  (Permissions)
                                </Typography>
                              ) : (
                                ''
                              )}
                              {column.id === 'action' ? (
                                <TooltipCustom
                                  title={
                                    !permissionsList.includes('user_can_edit') ? (
                                      <FormattedMessage {...language.getText('user.PERMISSIONS_LABEL')} />
                                    ) : (
                                      <FormattedMessage {...language.getText('user.EDIT_DETAILS')} />
                                    )
                                  }
                                >
                                  <span>
                                    <Button
                                      variant={column.variant}
                                      disabled={!permissionsList.includes('user_can_edit')}
                                      size={column.size}
                                      color={column.color}
                                    >
                                      EDIT
                                    </Button>
                                  </span>
                                </TooltipCustom>
                              ) : (
                                ''
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100, 200]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Paper>
            <br />
            <br />
            <br />
          </div>
        )}

        {(history.location.pathname === '/users/add' || history.location.pathname === '/users/edit') && (
          <LoadingHandler
            loading={
              addUserStatus.isPending ||
              editUserstatus.isPending ||
              updateRolesStatus.isPending ||
              addRoleStatus.isPending
            }
            isError={addUserStatus.isError && addUserStatus.error && addUserStatus.error.status === 401}
            content={
              openNewUserDialog && (
                <UserDialog
                  closeUserDialog={closeUserDialog}
                  saveUser={saveUser}
                  userRoles={userRoles}
                  closeShowAddEdit={closeShowAddEdit}
                  showAddUserText={showAddUserText}
                  showEditData={showEditData}
                  updateUser={updateUser}
                  match={match}
                />
              )
            }
            loadingContent={<Loader />}
            errorContent={<StateErrorDisplay error={addUserStatus.error} showDetails />}
          />
        )}
        {openUserPrivilegeDialog && <UserPrivilege closeUploadContent={closeUploadContent} />}
        {openNewRoleDialog && <RoleDialog closeRoleDialog={closeRoleDialog} saveRole={saveRole} />}
        {openRoleList && (
          <RoleList
            closeRoleList={closeRoleList}
            allRoles={allRoles}
            usersState={usersState}
            setOpenRoleList={setOpenRoleList}
          />
        )}
      </>
    </ValidatePermissions>
  );
};

UsersList.propTypes = {
  data: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  updateBreadcrumbs: PropTypes.func.isRequired,
  pathUrl: PropTypes.func.isRequired,
  usersState: shapes.modelOf(Users).isRequired,
  userRoles: shapes.modelOf(Roles).isRequired,
  match: PropTypes.object.isRequired,
  editUserstatus: shapes.state.isRequired,
  addUserStatus: shapes.state.isRequired,
  updateRolesStatus: shapes.state.isRequired,
  addRoleStatus: shapes.state.isRequired,
  userPermissions: shapes.modelOf(UsersPermissions).isRequired,
  language: shapes.language.isRequired,
  allRoles: shapes.modelOf(AllRoles).isRequired
};

export default withStyles(styles)(inject('language', 'userPermissions')(observer(UsersList)));
